<script setup lang="ts">
import BaseButton from "~/components/Base/BaseButton.vue";
// import BaseRating from "~/components/Base/BaseRating.vue";
import { useMainStore, useProductPageStore } from "~/stores/main";
import { useCartStore } from "~/stores/cartStore";
import { usePriceFormatter2, usePriceInCurrency } from "~/utils/priceFormatter";
import {
  addToCart as sendAddToCartEvent,
  viewItem as sendViewItemEvent,
} from "~/composables/useAnalytics";

// -----------------------
// props
// -----------------------
const props = withDefaults(
  defineProps<{
    store?: any;
    settingsWebshop?: any;
  }>(),
  {
    store: null,
    settingsWebshop: null
  },
);

// -----------------------
// composables
// -----------------------
const { status, data } = useAuth();
const store = props.store || useProductPageStore();
const mainStore = useMainStore();
const cartStore = useCartStore();
const { totalStockOfVariant, getAssetUrl, isRealContent } = useHelpers();
const { t } = useI18n();
const config = useRuntimeConfig();
const themeGroup = config.public.theme.group;
const settingsWebshopModule = props.settingsWebshop;

// -----------------------
// reactive properties
// -----------------------
const isSizeGuideModalVisibleRef = ref<boolean>(false);
// const isStoreStockModalVisibleRef = ref<boolean>(false);
const addingToBasketRef = ref<boolean>(false); // todo: put some spinner or similar to "add to basket" button based on this
const quantityRef = ref<number>(1);
// const isProductFavourite = ref<boolean>(false);
const isAddToCartButtonShaking = ref<boolean>(false);

// -----------------------
// computed properties
// -----------------------
const variantRef = computed(() => {
  if (store.size === null || store.color === null) {
    return null;
  }

  return store.masterProduct?.products.find((variant: Record<any, any>) => {
    return (
      variant.product.product_meret_property === store.size &&
      variant.product.product_szin_property === store.color
    );
  });
});

const maxQuantityRef = computed(() => {
  if (variantRef.value) {
    return totalStockOfVariant(variantRef.value);
  }

  return 0;
});

/*
const priceRef = computed(() => {
  if (variantRef.value) {
    // console.log(variantRef.value);
    if (status.value === "authenticated") {
      if (
        Array.isArray(data?.value?.usergroups) &&
        data?.value?.usergroups?.includes(1) &&
        Array.isArray(variantRef.value.product.prices) &&
        variantRef.value.product.prices.length > 0
      ) {
        return usePriceFormatter(
          {
            original_price: parseInt(variantRef.value.product.original_price),
            price: parseInt(variantRef.value.product.price),
            pricelist_original_price: parseFloat(
              variantRef.value.product.prices[0].original_price,
            ),
            pricelist_price: parseFloat(
              variantRef.value.product.prices[0].price,
            ),
          },
          "product",
        );
      }
    }

    return usePriceFormatter(
      {
        original_price: parseFloat(variantRef.value.product.original_price),
        price: parseFloat(variantRef.value.product.price),
      },
      "product",
    );
  }

  return usePriceFormatter(store.masterProduct, "masterProduct");
});
*/

const priceRef2 = computed(() => {
  const ret = {
    line_1: "",
    line_2: "",
    line_2_highlight: false,
    rule: "",
    pricelist_1_member: false,
    line_2_suffix: "",
  };

  if (status.value === "authenticated") {
    if (
      Array.isArray(data?.value?.usergroups) &&
      data?.value?.usergroups?.includes(1)
    ) {
      ret.pricelist_1_member = true;
    }
  }

  let priceData = null;
  if (variantRef.value) {
    priceData = usePriceFormatter2(variantRef.value.product, "product");
    ret.line_1 = priceData.line_1;
    ret.line_2 = priceData.line_2;
    ret.line_2_highlight = priceData.line_2_highlight;
    ret.rule = priceData.rule;
    ret.active_line = priceData.active_line;
  } else {
    priceData = usePriceFormatter2(store.masterProduct, "masterProduct");
    ret.line_1 = priceData.line_1;
    ret.line_2 = priceData.line_2;
    ret.line_2_highlight = priceData.line_2_highlight;
    ret.rule = priceData.rule;
    ret.active_line = priceData.active_line;
  }

  if (
    settingsWebshopModule && 
    settingsWebshopModule.global_percent_coupon_calculation_enabled && 
    Date.now() > settingsWebshopModule.global_percent_coupon_calculation_start_time &&
    Date.now() < settingsWebshopModule.global_percent_coupon_calculation_end_time &&
    parseInt(settingsWebshopModule.global_percent_coupon_calculation_discount_percent)>0
  ) {
    let applyIt = false;
    let inAction = false;
    if (variantRef.value) {
      if (variantRef.value.product.price != variantRef.value.product.original_price) inAction = true;
    } else {
      inAction = store.masterProduct.calculated_base_prices.in_action;
    }
    if (settingsWebshopModule.global_percent_coupon_calculation_only_inaction) {
      if (inAction) applyIt = true;
    } else {
      applyIt = true;
    }

    if (applyIt) {
      const globalPercentDiscountPrice = Math.round(priceData.line_2_price * (100 - settingsWebshopModule.global_percent_coupon_calculation_discount_percent) / 100);

      ret.line_2_suffix = t("product_page.global_percent_coupon_calculation_price2_suffix") + ": " + usePriceInCurrency(globalPercentDiscountPrice);
    }
  }

  return ret;
});

/*
const favouriteIconClass = computed(() => {
  return isProductFavourite.value
    ? "m-icon-favourite_filled"
    : "m-icon-favourite";
});
*/
const manufacturerLogo = computed(() => {
  const folder = config.public.routes.manufacturers;

  return store.manufacturer?.logo
    ? getAssetUrl(`Public/assets/webshop/${folder}/${store.manufacturer?.logo}`)
    : "";
});

const manufacturerSizechart = computed(() => {
  return store.manufacturer?.sizechart
    ? getAssetUrl(`Public/assets/${store.manufacturer?.sizechart}`)
    : "";
});

const isAddToCartDisabled = computed(() => {
  return (
    !store.color ||
    !store.size ||
    maxQuantityRef.value === 0 ||
    addingToBasketRef.value
  );
});

const addToCartButtonText = computed(() => {
  if (isAddToCartButtonShaking.value) {
    if (!store.size) {
      return t("product_page.select_size");
    } else if (maxQuantityRef.value === 0) {
      return t("out_of_stock");
    } else {
      return t("add_to_cart");
    }
  } else {
    return t("add_to_cart");
  }
});

const hasDescription = computed(() => {
  const description = store.masterProduct.description;

  return (
    description && typeof description === "string" && isRealContent(description)
  );
});

// -----------------------
// helper functions
// -----------------------

const addToBasket = async () => {
  // console.log('adding to basket');
  addingToBasketRef.value = true;

  if (store.selectedVariant) {
    try {
      const added = await cartStore.addProduct(
        store.selectedVariant,
        quantityRef.value,
      );

      if (added) {
        sendAddToCartEvent(
          store.masterProduct,
          variantRef.value,
          quantityRef.value,
        );

        // Cart data is already fetched in addProduct, no need to fetch again
        mainStore.cartVisible = true;
      }
    } catch (error) {
      // console.error("Failed to add product to cart:", error);
    } finally {
      addingToBasketRef.value = false;
    }
  } else {
    addingToBasketRef.value = false;
  }
};

/*
const toggleFavourite = () => {
  // TODO: add BE logic here
  isProductFavourite.value = !isProductFavourite.value;
};
*/

const urgeUserToSelect = () => {
  isAddToCartButtonShaking.value = true;

  setTimeout(() => {
    isAddToCartButtonShaking.value = false;
  }, 1000);
};
</script>

<template>
  <div class="col-span-2 lg:col-span-1 px-4 pt-5">
    <BaseModal
      :width="700"
      :height="400"
      :show="isSizeGuideModalVisibleRef"
      @close="isSizeGuideModalVisibleRef = false"
    >
      <NuxtImg :src="manufacturerSizechart" format="webp" loading="lazy" />
    </BaseModal>

    <!-- Manufacturer & product name -->
    <div class="flex justify-between items-center">
      <div v-if="themeGroup === 'mountex'" class="text-brand-headline">
        <h1 class="!text-2xl lg:!text-3xl !mt-[0] font-bold mb-1">
          {{ store.masterProduct?.masterproduct_gyarto_property }}
        </h1>
        <h2 class="!text-base lg:!text-2xl !mt-0">
          {{ store.masterProduct?.name }}
        </h2>
      </div>
      <h1 v-else class="font-bold !text-2xl lg:!text-3xl xl:!text-4xl !mt-[0]">
        {{ store.masterProduct?.name }}
      </h1>

      <div v-if="store.manufacturer && manufacturerLogo" class="ml-[50px]">
        <NuxtImg
          v-if="config.public.theme.group === 'tnf'"
          :alt="store.manufacturer.name"
          :src="manufacturerLogo"
          loading="lazy"
          width="120"
        />
        <a
          v-else
          :href="`/${config.public.routes.manufacturers}/${store.manufacturer.slug}`"
        >
          <NuxtImg
            class="cursor-pointer"
            :alt="store.manufacturer.name"
            :src="manufacturerLogo"
            loading="lazy"
            width="120"
          />
        </a>
      </div>
    </div>

    <div class="!text-xs lg:!text-base">
      <!-- Rating: TBD -->
      <!-- <BaseRating :rating="4" /> -->

      <!-- SKU -->
      <div
        v-if="store.masterProduct?.stock"
        class="pt-1 mb-1 lg:pt-2 xl:pt-2.5 lg:mb-3 xl:mb-2"
      >
        {{ t("sku") }}:
        {{ store.selectedVariantSku || t("product_page.select_size") }}
      </div>

      <!-- Price(s) -->
      <div :class="priceRef2?.line_2_suffix ? 'h-[102px] md:h-[74px]' : 'h-[74px]'">
        <div v-if="store.masterProduct?.stock" class="text-xl">
          <ClientOnly>
            <template v-if="priceRef2">
              <div
                style="height: 32px; margin-bottom: 10px"
                :class="[
                  'inline-flex items-center justify-center', // always included
                  { 'text-2xl': priceRef2.active_line === 1 }, // conditional
                ]"
              >
                <!-- eslint-disable vue/no-v-html -->
                <span :title="priceRef2.rule" v-html="priceRef2.line_1" />
                <!-- eslint-enable -->
              </div>
              <div style="height: 32px">
                <span
                  v-if="priceRef2.line_2_highlight"
                  style="height: 32px"
                  class="bg-brand-membership inline-flex items-center justify-center px-3"
                >
                  <!-- eslint-disable vue/no-v-html -->
                  <span
                    :class="[
                      '', // always included
                      { 'lg:text-2xl': priceRef2.active_line === 2 }, // conditional
                    ]"
                    v-html="priceRef2.line_2"
                  />
                  <!-- eslint-enable -->
                </span>
                <span v-else>
                  <span
                    :class="[
                      '', // always included
                      { 'text-2xl': priceRef2.active_line === 2 }, // conditional
                    ]"
                    v-html="priceRef2.line_2"
                  />
                </span>
                <span v-if="priceRef2.line_2_suffix" class="block md:inline md:ml-3">{{ priceRef2.line_2_suffix }}</span>
              </div>
            </template>
          </ClientOnly>
        </div>
      </div>
    </div>

    <ProductPageVariants
      :size-guide="manufacturerSizechart !== ''"
      @show-size-guide="isSizeGuideModalVisibleRef = true"
      @load="sendViewItemEvent"
      @select="sendViewItemEvent"
    ></ProductPageVariants>

    <!-- Cart button -->
    <div
      v-if="store.masterProduct?.stock ?? 0 > 0"
      class="add-to-cart-container relative flex justify-center h-[50px] lg:h-[56px]"
    >
      <BaseButton
        type="primary"
        class="add-to-cart text-xl px-[60px] bg-brand-search min-h-[50px] lg:min-h-[56px]"
        :class="{ shaking: isAddToCartButtonShaking }"
        :disabled="isAddToCartDisabled"
        @click="addToBasket()"
        >{{ addToCartButtonText }}</BaseButton
      >
      <div
        v-if="isAddToCartDisabled"
        class="absolute w-full h-full cursor-not-allowed z-20 top-0"
        @click.capture="urgeUserToSelect"
      ></div>
    </div>
    <div v-else>
      <BaseButton type="green" class="text-xl mt-[25px]" :disabled="true">{{
        t("non_orderable")
      }}</BaseButton>
    </div>
    <div v-if="store.masterProduct?.stock ?? 0 > 0">
      <ProductPageStockInfoROSK
        v-if="
          ['mountexro', 'mountexsk'].includes(
            useRuntimeConfig().public.theme.site,
          )
        "
      />
      <ProductPageStockInfo v-else />
      <ProductPageShippingDetails />
    </div>
    <div v-else class="mt-5 text-brand-highlight">
      <span class="m-icon-cancel mr-5" />{{ t("out_of_stock") }}
    </div>
    <ProductPageDescription v-if="hasDescription" />
    <ProductPageAttributes />
  </div>
</template>

<style scoped>
@keyframes horizontal-shaking {
  0% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(7px);
  }
  50% {
    transform: translateX(-7px);
  }
  75% {
    transform: translateX(7px);
  }
  100% {
    transform: translateX(0);
  }
}

.add-to-cart.shaking {
  animation-name: horizontal-shaking;
  animation-duration: 400ms;
  animation-delay: 200ms;
  animation-iteration-count: 2;
}
.add-to-cart-container,
.add-to-cart:not(:disabled) {
  transition-property: width, height;
  transition-duration: 500ms;
}

.add-to-cart {
  width: 100%;
}
</style>
